/**
 * Dev-only моковый лид для локальной проверки UI без бэкенда.
 *
 * Активируется при вводе телефона `MOCK_LEAD_PHONE` в LoginCallPanel.
 * После активации перехватываем основные эндпоинты в `apiClient` и возвращаем
 * canned-ответы; UI пробегает реальный auth-флоу (call IVR → polling → complete →
 * session → dashboard) и оказывается на личном кабинете лида с моковыми данными.
 *
 * Гарантии безопасности:
 *  1. Весь модуль вызывается только под `import.meta.env.DEV === true` —
 *     production-сборки tree-shake'ят его.
 *  2. Активация требует явного ввода `MOCK_LEAD_PHONE` пользователем.
 *  3. Логаут (`POST /api/auth/logout`) сбрасывает мок-флаг.
 *
 * Чтобы вручную сбросить, если что-то залипло:
 *   localStorage.removeItem('__pg19_mock_lead__')
 */

import type { LeadStatusDto } from '../types/lead'

export const MOCK_LEAD_PHONE = '79990000000'
const MOCK_FLAG_KEY = '__pg19_mock_lead__'
const MOCK_TOKEN = 'mock-lead-token-123'
const MOCK_CALL_NUMBER = '+7 (800) 555-99-99'

const mockLeadUserDto = {
  id: '999',
  firstName: 'Иван',
  lastName: 'Лидов',
  middleName: '',
  phone: MOCK_LEAD_PHONE,
  email: '',
  telegram: '',
  telegramId: null,
  vkId: '',
  max: '',
  maxId: null,
  avatar: null,
  birthDate: null,
  role: 'user',
  clientKind: 'lead' as const,
}

const mockLeadDto: LeadStatusDto = {
  id: 'lead-mock',
  status: 'in_review',
  statusLabel: 'Заявка обрабатывается',
  address: 'г Таганрог, ул Тестовая, д 1, кв 1 (моковая заявка)',
  requestedTariff: 'Тариф 100 Мбит/с',
  plannedDate: null,
  comment: null,
  createdAt: new Date(Date.now() - 24 * 3600 * 1000).toISOString(),
}

function isMockActive(): boolean {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem(MOCK_FLAG_KEY) === '1'
  } catch {
    return false
  }
}

function activateMock(): void {
  try {
    localStorage.setItem(MOCK_FLAG_KEY, '1')
  } catch { /* private mode */ }
}

function deactivateMock(): void {
  try {
    localStorage.removeItem(MOCK_FLAG_KEY)
  } catch { /* private mode */ }
}

function jsonResponse(body: unknown, status = 200): Response {
  return new Response(JSON.stringify(body), {
    status,
    headers: { 'content-type': 'application/json' },
  })
}

function readJsonBody(body: BodyInit | null | undefined): unknown {
  if (typeof body !== 'string') return undefined
  try {
    return JSON.parse(body)
  } catch {
    return undefined
  }
}

/**
 * Возвращает canned `Response`, если запрос соответствует мок-сценарию лида.
 * Иначе — `null` (apiClient выполнит реальный fetch).
 */
export function tryMockLead(path: string, options?: RequestInit): Response | null {
  // Активация — телефон в теле запроса инициирующего IVR
  if (path === '/api/auth/call-verify/request' && options?.method === 'POST') {
    const body = readJsonBody(options.body) as { phone?: string } | undefined
    if (body?.phone === MOCK_LEAD_PHONE) {
      activateMock()
      return jsonResponse({
        token: MOCK_TOKEN,
        callNumber: MOCK_CALL_NUMBER,
        expiresAt: new Date(Date.now() + 120_000).toISOString(),
        expiresInSeconds: 120,
      })
    }
    return null
  }

  if (!isMockActive()) return null

  // Polling — мгновенно verified, чтобы не ждать
  if (path.startsWith('/api/auth/call-verify/status/')) {
    return jsonResponse({ status: 'verified' })
  }

  // Complete — отдаём лид-сессию (account = null, потому что договора нет)
  if (path.startsWith('/api/auth/call-verify/complete/')) {
    return jsonResponse({
      success: true,
      user: mockLeadUserDto,
      account: null,
    })
  }

  // AuthGuard и refresh
  if (path === '/api/auth/session') {
    return jsonResponse({ user: mockLeadUserDto, account: null })
  }

  if (path === '/api/auth/refresh' && options?.method === 'POST') {
    return jsonResponse({ ok: true })
  }

  // Лид-данные
  if (path === '/api/lead/me') {
    return jsonResponse(mockLeadDto)
  }

  // /api/user/me — отдаём того же лида
  if (path === '/api/user/me') {
    return jsonResponse({ user: mockLeadUserDto })
  }

  // Новости — пустой ответ, чтобы дашборд не падал
  if (path.startsWith('/api/news')) {
    return jsonResponse({ news: [], total: 0 })
  }

  // Логаут — сбрасываем мок-флаг и возвращаем ok
  if (path === '/api/auth/logout' && options?.method === 'POST') {
    deactivateMock()
    return jsonResponse({ ok: true })
  }

  return null
}
